import React, { useEffect, useState } from 'react';

import { useGetStream } from '@vlabs/api/artemy/client';
import { StreamTargetsParameterItem } from '@vlabs/api/artemy/model';
import type { StreamAnalytics, StreamId } from '@vlabs/api/artemy/model';
import { PercentIcon, PixelsIcon, DeleteIcon } from '@vlabs/icons';
import { UUID_PATTERN } from '@vlabs/shared/validators';
import { ButtonSwitch, ButtonSwitchGroup, Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import i18next from 'i18next';
import type { Control as RHFControl, UseFormRegister } from 'react-hook-form';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import {
  getAnalyticsTypeLabel,
  loadHandlerOptions,
  parseAnalyticsLabel,
  SynchronizeStatus,
} from '@vlabs/monokit/entities/sources';
import type { HandlerOption } from '@vlabs/monokit/entities/sources';
import { numberInputValue } from '@vlabs/monokit/shared/lib/utils';
import {
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  AccordionRoot,
  AccordionTrigger,
} from '@vlabs/monokit/shared/ui/accordion';
import { confirmDeletion } from '@vlabs/monokit/shared/ui/confirm-deletion';
import { FormFieldGroup } from '@vlabs/monokit/shared/ui/form-field-group';
import { selectMenuPortalStyles } from '@vlabs/monokit/shared/ui/select-portal';
import { AnalyticStatus } from '@vlabs/monokit/shared/ui/stream-statuses';

import st from './FacestreamAnalytic.module.sass';
import {
  getFacestreamAnalyticStrictTranslation,
  useFacestreamAnalyticTranslation,
} from './i18n';
import type { RoiRect } from './roiCoordinates';
import {
  formatRoiCoordinates,
  getRoiRectMode,
  mergeRoiRect,
  parseRoiCoordinates,
} from './roiCoordinates';

// Форма-носитель массива ВА: совместимы GroupSettingsFormValues и StreamSettingsFormValues
export interface FacestreamAnalyticFormValues {
  analytics: StreamAnalytics[];
}

interface FacestreamAnalyticProps {
  index: number;
  /** Удаление ВА: в потоке — только личные (не унаследованные). */
  allowDelete?: boolean;
  /** Поток, для которого показываем бейдж статуса; в группе не передаётся. */
  streamId?: StreamId;
  /** Секция ROI/DROI с редактированием координат текстом (фолбэк без ретрансляции). */
  showRoiDroi?: boolean;
  /** Пофилдовые статусы наследования параметров ВА с группой; undefined — иконки не показываются. */
  getSyncStatus?: (paramPath: string) => boolean | undefined;
}

interface Option<TValue extends string | number> {
  value: TValue;
  label: string;
}

interface FieldProps {
  base: string;
  name: string;
  label: string;
  tooltip?: string;
  register: UseFormRegister<FacestreamAnalyticFormValues>;
  type?: 'number';
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Sync-иконка рядом с лейблом (необязательна). */
  syncIcon?: React.JSX.Element;
}

function InputRow({
  base,
  name,
  label,
  tooltip = undefined,
  register,
  type = undefined,
  min = undefined,
  max = undefined,
  disabled = false,
  syncIcon = undefined,
}: FieldProps): React.JSX.Element {
  return (
    <FormFieldGroup.Item
      label={(
        <React.Fragment>
          {label}
          {syncIcon}
        </React.Fragment>
      )}
      tooltip={tooltip}
    >
      <Control.Input
        disabled={disabled}
        max={max}
        min={min}
        type={type}
        {...register(`${base}.${name}`, type === 'number' ? { setValueAs: numberInputValue } : {})}
      />
    </FormFieldGroup.Item>
  );
}

function SwitchRow({
  base,
  name,
  label,
  tooltip = undefined,
  control,
  syncIcon = undefined,
}: {
  base: string;
  name: string;
  label: string;
  tooltip?: string;
  control: RHFControl<FacestreamAnalyticFormValues>;
  syncIcon?: React.JSX.Element;
}): React.JSX.Element {
  return (
    <FormFieldGroup.Item
      label={(
        <React.Fragment>
          {label}
          {syncIcon}
        </React.Fragment>
      )}
      tooltip={tooltip}
    >
      <Control.Switch
        control={control}
        name={`${base}.${name}`}
      />
    </FormFieldGroup.Item>
  );
}

function SelectRow<TValue extends string | number>({
  base,
  name,
  label,
  tooltip = undefined,
  options,
  control,
  isClearable = false,
  placeholder = undefined,
  syncIcon = undefined,
}: {
  base: string;
  name: string;
  label: string;
  tooltip?: string;
  options: Option<TValue>[];
  control: RHFControl<FacestreamAnalyticFormValues>;
  isClearable?: boolean;
  placeholder?: string;
  syncIcon?: React.JSX.Element;
}): React.JSX.Element {
  return (
    <FormFieldGroup.Item
      label={(
        <React.Fragment>
          {label}
          {syncIcon}
        </React.Fragment>
      )}
      tooltip={tooltip}
    >
      <Controller
        control={control}
        name={`${base}.${name}`}
        render={({ field: { value, onChange } }) => (
          <Select
            isClearable={isClearable}
            menuPortalTarget={document.body}
            onChange={(option) => onChange(option?.value ?? undefined)}
            options={options}
            placeholder={placeholder}
            styles={selectMenuPortalStyles}
            value={options.find((option) => option.value === value)}
          />
        )}
      />
    </FormFieldGroup.Item>
  );
}

function confirmDeleteAnalytics(onConfirm: VoidFunction): void {
  confirmDeletion({
    cancelLabel: i18next.t('entities:facestreamAnalytic.delete_confirm.buttons.cancel'),
    confirmLabel: i18next.t('entities:facestreamAnalytic.delete_confirm.buttons.delete'),
    icon: <DeleteIcon />,
    onConfirm,
    subtitle: i18next.t('entities:facestreamAnalytic.delete_confirm.subtitle'),
    title: i18next.t('entities:facestreamAnalytic.delete_confirm.title'),
  });
}

interface RoiDroiRowProps {
  label: string;
  tooltip?: string;
  value: unknown;
  onChange: (next: RoiRect) => void;
}

// Редактирование координат ROI/DROI текстом + переключатель единиц измерения —
// фолбэк на случай недоступности ретрансляции (открытый вопрос GUI-3863).
function RoiDroiRow({
  label,
  tooltip = undefined,
  value,
  onChange,
}: RoiDroiRowProps): React.JSX.Element {
  const { t } = useFacestreamAnalyticTranslation();
  const [text, setText] = useState(formatRoiCoordinates(value));

  useEffect(() => {
    setText(formatRoiCoordinates(value));
  }, [value]);

  const commit = () => {
    const parsed = parseRoiCoordinates(text);
    if (parsed) {
      onChange(mergeRoiRect(value, { ...parsed, mode: getRoiRectMode(value) }));
      return;
    }

    setText(formatRoiCoordinates(value));
  };

  const mode = getRoiRectMode(value);

  return (
    <FormFieldGroup.Item
      label={label}
      tooltip={tooltip}
    >
      <div className={st.RoiDroi}>
        <Control.Input
          onBlur={commit}
          onChange={(event) => setText(event.target.value)}
          value={text}
        />

        <ButtonSwitchGroup
          onChange={(nextMode) => onChange(mergeRoiRect(value, { mode: nextMode }))}
          size="small"
          value={mode}
          variant="round"
        >
          <ButtonSwitch
            icon={<PixelsIcon />}
            title={t('sections.roi_droi.pixels')}
            value="abs"
          />
          <ButtonSwitch
            icon={<PercentIcon />}
            title={t('sections.roi_droi.percent')}
            value="percent"
          />
        </ButtonSwitchGroup>
      </div>
    </FormFieldGroup.Item>
  );
}

function FacestreamAnalytic({
  index,
  allowDelete = true,
  streamId = undefined,
  showRoiDroi = false,
  getSyncStatus = undefined,
}: FacestreamAnalyticProps): React.JSX.Element {
  const { t } = useFacestreamAnalyticTranslation();
  // Кросс-ns ключи типов ВА не резолвятся через префиксный t
  const {
    register, control, watch, setValue,
  } = useFormContext<FacestreamAnalyticFormValues>();
  const { remove } = useFieldArray({ control, name: 'analytics' });

  // Бейдж статуса потока — самодостаточно: только страница потока передаёт streamId
  const { data: statusStream } = useGetStream(
    streamId ?? '',
    { targets: [StreamTargetsParameterItem.status] },
    { query: { enabled: !!streamId } },
  );

  // Опции обработчиков — для отображения названия выбранного handler_id
  const [handlerOptions, setHandlerOptions] = useState<HandlerOption[]>([]);

  useEffect(() => {
    loadHandlerOptions().then(setHandlerOptions);
  }, []);

  const base = `analytics.${index}.parameters.parameters`;
  const analytics = watch('analytics')[index];

  // Элемент удалён, но секция ещё не переключена — не рендерим форму,
  // чтобы register/Controller не материализовали пути удалённого элемента в values
  if (!analytics) return null;

  // Sync-иконка для параметра ВА: путь относительно parameters.parameters
  const syncIconFor = (paramPath: string): React.JSX.Element | undefined => {
    const synchronized = getSyncStatus?.(paramPath);
    if (synchronized === undefined) return undefined;

    return <SynchronizeStatus synchronized={synchronized} />;
  };

  const labelParts = parseAnalyticsLabel(analytics?.label);
  const title = getAnalyticsTypeLabel(analytics?.type ?? '')
    + (labelParts ? ` ${labelParts.number}` : '');

  const confirmDelete = () => {
    confirmDeleteAnalytics(() => {
      remove(index);
      toast.success(t('on_delete_success'));
    });
  };

  const numericOptions = (values: number[], labelKeyPrefix: string): Option<number>[] => (
    values.map((value) => ({ value, label: t(`options.${labelKeyPrefix}_${value}`) }))
  );

  const accordionDefaults = ['basic', 'sending', 'filtering', 'liveness', 'healthcheck', 'primary_track'];
  if (showRoiDroi) accordionDefaults.splice(1, 0, 'roi_droi');

  return (
    <div className={st.Root}>
      <div className={st.Header}>
        <div className={st.HeaderTitle}>
          <h2 className={st.Title}>{title}</h2>
          {statusStream?.status !== undefined && <AnalyticStatus status={statusStream.status} />}
        </div>

        {!!allowDelete && (
          <Control.Button
            icon={<DeleteIcon />}
            kind="negative"
            onClick={confirmDelete}
            size="small"
            variant="flat"
          >
            {t('delete_analytics')}
          </Control.Button>
        )}
      </div>

      <AccordionRoot
        className={st.Accordion}
        defaultValue={accordionDefaults}
      >
        <AccordionItem value="basic">
          <AccordionHeader>
            <AccordionTrigger>{t('sections.basic.title')}</AccordionTrigger>
          </AccordionHeader>
          <AccordionPanel>
            <FormFieldGroup>
              <SelectRow
                base={base}
                control={control}
                label={t('sections.basic.mode.label')}
                name="mode"
                options={numericOptions([1, 2, 3], 'mode')}
                syncIcon={syncIconFor('mode')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.basic.mode.tooltip')}
              />
              <FormFieldGroup.Item
                label={(
                  <React.Fragment>
                    {t('sections.basic.handler.label')}
                    {syncIconFor('event_handler.handler_id')}
                  </React.Fragment>
                )}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.basic.handler.tooltip')}
              >
                <Controller
                  control={control}
                  name={`${base}.event_handler.handler_id`}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      defaultOptions
                      isAsync
                      loadOptions={loadHandlerOptions}
                      menuPortalTarget={document.body}
                      onChange={(option) => onChange(option?.value ?? '')}
                      placeholder={t('sections.basic.handler.placeholder')}
                      styles={selectMenuPortalStyles}
                      value={value
                        ? handlerOptions.find((option) => option.value === value) ?? { label: value, value }
                        : undefined}
                    />
                  )}
                  rules={{
                    required: t('validation.required'),
                    pattern: { message: t('validation.uuid'), value: UUID_PATTERN },
                  }}
                />
              </FormFieldGroup.Item>
              <SelectRow
                base={base}
                control={control}
                label={t('sections.basic.frame_processing_mode.label')}
                name="frame_processing_mode"
                options={[
                  { value: 'auto', label: t('options.frame_auto') },
                  { value: 'full', label: t('options.frame_full') },
                  { value: 'scale', label: t('options.frame_scale') },
                ]}
                syncIcon={syncIconFor('frame_processing_mode')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.basic.frame_processing_mode.tooltip')}
              />
              <InputRow
                base={base}
                label={t('sections.basic.ffmpeg_threads_number.label')}
                name="ffmpeg_threads_number"
                register={register}
                syncIcon={syncIconFor('ffmpeg_threads_number')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.basic.ffmpeg_threads_number.tooltip')}
                type="number"
              />
              <InputRow
                base={base}
                label={t('sections.basic.real_time_mode_fps.label')}
                name="real_time_mode_fps"
                register={register}
                syncIcon={syncIconFor('real_time_mode_fps')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.basic.real_time_mode_fps.tooltip')}
                type="number"
              />
              <InputRow
                base={base}
                label={t('sections.basic.preferred_width.label')}
                name="preferred_program_stream_frame_width"
                register={register}
                syncIcon={syncIconFor('preferred_program_stream_frame_width')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.basic.preferred_width.tooltip')}
                type="number"
              />
            </FormFieldGroup>
          </AccordionPanel>
        </AccordionItem>

        {!!showRoiDroi && (
          <AccordionItem value="roi_droi">
            <AccordionHeader>
              <AccordionTrigger>{t('sections.roi_droi.title')}</AccordionTrigger>
            </AccordionHeader>
            <AccordionPanel>
              <FormFieldGroup>
                <RoiDroiRow
                  label={t('sections.roi_droi.roi.label')}
                  onChange={(next) => setValue(`${base}.roi`, next, { shouldDirty: true })}
                  tooltip={getFacestreamAnalyticStrictTranslation('sections.roi_droi.roi.tooltip')}
                  value={analytics?.parameters?.parameters?.roi}
                />
                <RoiDroiRow
                  label={t('sections.roi_droi.droi.label')}
                  onChange={(next) => setValue(`${base}.droi`, next, { shouldDirty: true })}
                  tooltip={getFacestreamAnalyticStrictTranslation('sections.roi_droi.droi.tooltip')}
                  value={analytics?.parameters?.parameters?.droi}
                />
              </FormFieldGroup>
            </AccordionPanel>
          </AccordionItem>
        )}

        <AccordionItem value="sending">
          <AccordionHeader>
            <AccordionTrigger>{t('sections.sending.title')}</AccordionTrigger>
          </AccordionHeader>
          <AccordionPanel>
            <FormFieldGroup>
              <SelectRow
                base={`${base}.sending`}
                control={control}
                label={t('sections.sending.bestshot_type.label')}
                name="bestshot_settings.type"
                options={numericOptions([1, 2, 3, 4], 'bestshot')}
                syncIcon={syncIconFor('sending.bestshot_settings.type')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.sending.bestshot_type.tooltip')}
              />
              <InputRow
                base={`${base}.sending.bestshot_settings`}
                label={t('sections.sending.face_bestshots.label')}
                name="face_bestshots_to_send"
                register={register}
                syncIcon={syncIconFor('sending.bestshot_settings.face_bestshots_to_send')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.sending.face_bestshots.tooltip')}
                type="number"
              />
              <InputRow
                base={`${base}.sending.bestshot_settings`}
                label={t('sections.sending.body_bestshots.label')}
                name="body_bestshots_to_send"
                register={register}
                syncIcon={syncIconFor('sending.bestshot_settings.body_bestshots_to_send')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.sending.body_bestshots.tooltip')}
                type="number"
              />
              <SwitchRow
                base={`${base}.sending.body`}
                control={control}
                label={t('sections.sending.send_only_full_set.label')}
                name="send_only_full_set"
                syncIcon={syncIconFor('sending.body.send_only_full_set')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.sending.send_only_full_set.tooltip')}
              />
              <SwitchRow
                base={`${base}.sending.body`}
                control={control}
                label={t('sections.sending.delete_track_after_sending.label')}
                name="delete_track_after_sending"
                syncIcon={syncIconFor('sending.body.delete_track_after_sending')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.sending.delete_track_after_sending.tooltip')}
              />
              <SelectRow
                base={`${base}.sending`}
                control={control}
                label={t('sections.sending.full_frame_settings.label')}
                name="full_frame_settings"
                options={numericOptions([1, 2, 3], 'bestshot')}
                syncIcon={syncIconFor('sending.full_frame_settings')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.sending.full_frame_settings.tooltip')}
              />
              <SelectRow
                base={`${base}.sending`}
                control={control}
                label={t('sections.sending.sending_type.label')}
                name="type"
                options={[
                  { value: 'sec', label: t('options.sending_sec') },
                  { value: 'frames', label: t('options.sending_frames') },
                ]}
                syncIcon={syncIconFor('sending.type')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.sending.sending_type.tooltip')}
              />
              <InputRow
                base={`${base}.sending`}
                label={t('sections.sending.time_period_of_searching.label')}
                name="time_period_of_searching"
                register={register}
                syncIcon={syncIconFor('sending.time_period_of_searching')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.sending.time_period_of_searching.tooltip')}
                type="number"
              />
              <InputRow
                base={`${base}.sending`}
                label={t('sections.sending.silent_period.label')}
                name="silent_period"
                register={register}
                syncIcon={syncIconFor('sending.silent_period')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.sending.silent_period.tooltip')}
                type="number"
              />
            </FormFieldGroup>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem value="filtering">
          <AccordionHeader>
            <AccordionTrigger>{t('sections.filtering.title')}</AccordionTrigger>
          </AccordionHeader>
          <AccordionPanel>
            <FormFieldGroup>
              <InputRow
                base={`${base}.filtering`}
                label={t('sections.filtering.min_score_face.label')}
                name="min_score_face"
                register={register}
                syncIcon={syncIconFor('filtering.min_score_face')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.filtering.min_score_face.tooltip')}
                type="number"
              />
              <InputRow
                base={`${base}.filtering`}
                label={t('sections.filtering.min_score_body.label')}
                name="min_score_body"
                register={register}
                syncIcon={syncIconFor('filtering.min_score_body')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.filtering.min_score_body.tooltip')}
                type="number"
              />
              <InputRow
                base={`${base}.filtering`}
                label={t('sections.filtering.detection_yaw_threshold.label')}
                name="detection_yaw_threshold"
                register={register}
                syncIcon={syncIconFor('filtering.detection_yaw_threshold')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.filtering.detection_yaw_threshold.tooltip')}
                type="number"
              />
              <InputRow
                base={`${base}.filtering`}
                label={t('sections.filtering.detection_pitch_threshold.label')}
                name="detection_pitch_threshold"
                register={register}
                syncIcon={syncIconFor('filtering.detection_pitch_threshold')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.filtering.detection_pitch_threshold.tooltip')}
                type="number"
              />
              <InputRow
                base={`${base}.filtering`}
                label={t('sections.filtering.detection_roll_threshold.label')}
                name="detection_roll_threshold"
                register={register}
                syncIcon={syncIconFor('filtering.detection_roll_threshold')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.filtering.detection_roll_threshold.tooltip')}
                type="number"
              />
              <SwitchRow
                base={`${base}.filtering`}
                control={control}
                label={t('sections.filtering.yaw_collection_mode.label')}
                name="yaw_collection_mode"
                syncIcon={syncIconFor('filtering.yaw_collection_mode')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.filtering.yaw_collection_mode.tooltip')}
              />
              <InputRow
                base={`${base}.filtering`}
                label={t('sections.filtering.yaw_number.label')}
                name="yaw_number"
                register={register}
                syncIcon={syncIconFor('filtering.yaw_number')}
                type="number"
              />
              <InputRow
                base={`${base}.filtering`}
                label={t('sections.filtering.mouth_occlusion_threshold.label')}
                name="mouth_occlusion_threshold"
                register={register}
                syncIcon={syncIconFor('filtering.mouth_occlusion_threshold')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.filtering.mouth_occlusion_threshold.tooltip')}
                type="number"
              />
              <InputRow
                base={`${base}.filtering`}
                label={t('sections.filtering.min_body_size_threshold.label')}
                name="min_body_size_threshold"
                register={register}
                syncIcon={syncIconFor('filtering.min_body_size_threshold')}
                tooltip={getFacestreamAnalyticStrictTranslation('sections.filtering.min_body_size_threshold.tooltip')}
                type="number"
              />
            </FormFieldGroup>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem value="liveness">
          <AccordionHeader>
            <AccordionTrigger>{t('sections.liveness.title')}</AccordionTrigger>
          </AccordionHeader>
          <AccordionPanel>
            <FormFieldGroup>
              <InputRow
                base={`${base}.liveness.livenesses_weights`}
                label={t('sections.liveness.liveness_weight_1.label')}
                name="0"
                register={register}
                syncIcon={syncIconFor('liveness.livenesses_weights')}
                type="number"
              />
              <InputRow
                base={`${base}.liveness.livenesses_weights`}
                label={t('sections.liveness.liveness_weight_2.label')}
                name="1"
                register={register}
                syncIcon={syncIconFor('liveness.livenesses_weights')}
                type="number"
              />
              <SwitchRow
                base={`${base}.liveness`}
                control={control}
                label={t('sections.liveness.mask_liveness.label')}
                name="use_mask_liveness_filtration"
                syncIcon={syncIconFor('liveness.use_mask_liveness_filtration')}
              />
              <SwitchRow
                base={`${base}.liveness`}
                control={control}
                label={t('sections.liveness.flying_liveness.label')}
                name="use_flying_faces_liveness_filtration"
                syncIcon={syncIconFor('liveness.use_flying_faces_liveness_filtration')}
              />
              <SelectRow
                base={`${base}.liveness`}
                control={control}
                label={t('sections.liveness.liveness_mode.label')}
                name="liveness_mode"
                options={numericOptions([0, 1, 2], 'liveness')}
                syncIcon={syncIconFor('liveness.liveness_mode')}
              />
              <InputRow
                base={`${base}.liveness`}
                label={t('sections.liveness.number_of_liveness_checks.label')}
                name="number_of_liveness_checks"
                register={register}
                syncIcon={syncIconFor('liveness.number_of_liveness_checks')}
                type="number"
              />
              <InputRow
                base={`${base}.liveness`}
                label={t('sections.liveness.liveness_threshold.label')}
                name="liveness_threshold"
                register={register}
                syncIcon={syncIconFor('liveness.liveness_threshold')}
                type="number"
              />
              <InputRow
                base={`${base}.liveness`}
                label={t('sections.liveness.mask_backgrounds_count.label')}
                name="mask_backgrounds_count"
                register={register}
                syncIcon={syncIconFor('liveness.mask_backgrounds_count')}
                type="number"
              />
            </FormFieldGroup>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem value="healthcheck">
          <AccordionHeader>
            <AccordionTrigger>{t('sections.healthcheck.title')}</AccordionTrigger>
          </AccordionHeader>
          <AccordionPanel>
            <FormFieldGroup>
              <InputRow
                base={`${base}.healthcheck`}
                label={t('sections.healthcheck.healthcheck_period.label')}
                name="period"
                register={register}
                syncIcon={syncIconFor('healthcheck.period')}
                type="number"
              />
              {/* ponytail: второе поле блока уточняется у аналитика (GOAL GUI-3948) */}
            </FormFieldGroup>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem value="primary_track">
          <AccordionHeader>
            <AccordionTrigger>{t('sections.primary_track.title')}</AccordionTrigger>
          </AccordionHeader>
          <AccordionPanel>
            <FormFieldGroup>
              <SwitchRow
                base={`${base}.primary_track_policy`}
                control={control}
                label={t('sections.primary_track.use_primary_track.label')}
                name="use_primary_track_policy"
                syncIcon={syncIconFor('primary_track_policy.use_primary_track_policy')}
              />
              <InputRow
                base={`${base}.primary_track_policy`}
                label={t('sections.primary_track.best_shot_min_size.label')}
                name="best_shot_min_size"
                register={register}
                syncIcon={syncIconFor('primary_track_policy.best_shot_min_size')}
                type="number"
              />
              <InputRow
                base={`${base}.primary_track_policy`}
                label={t('sections.primary_track.best_shot_proper_size.label')}
                name="best_shot_proper_size"
                register={register}
                syncIcon={syncIconFor('primary_track_policy.best_shot_proper_size')}
                type="number"
              />
            </FormFieldGroup>
          </AccordionPanel>
        </AccordionItem>
      </AccordionRoot>
    </div>
  );
}

export { FacestreamAnalytic };
